import { Link } from '@inertiajs/react'
import {
  LoaderCircle,
  Pause,
  Pencil,
  Play,
  RotateCcw,
  SkipBack,
  SkipForward,
  SlidersHorizontal,
  Square,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { useScenePlayer } from '@/hooks/use-scene-player'
import { adminLines } from '@/routes'

/**
 * Sticky transport while a scene plays: what's being said, pause, step
 * between lines, line-by-line or continuous, and fixing the current line.
 */
export function PlaybackBar({
  player,
  productionId,
  scenes,
  onEdit,
}: {
  player: ReturnType<typeof useScenePlayer>
  productionId: string
  scenes: { id: number; title: string }[]
  /** Edit the current line in place; without it, "Fix" links to the line's page. */
  onEdit?: (lineId: number) => void
}) {
  if (player.status === 'idle') return null
  const scene = scenes.find((s) => s.id === player.sceneId)
  const line = player.current?.line
  const spoken = line && line.kind !== 'direction' && line.kind !== 'sound'
  const loading = player.status === 'loading'
  const lineByLine = player.mode === 'line'
  const status = loading
    ? 'Loading audio…'
    : player.finished
      ? `${scene?.title ?? ''} · Space for the next line, R to replay`
      : player.status === 'paused'
        ? `${scene?.title ?? ''} · paused`
        : scene?.title
  return (
    <div className="sticky bottom-4 z-20 mx-auto grid w-full max-w-3xl gap-2 rounded-xl border bg-background/95 p-3 shadow-lg backdrop-blur">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-0.5">
          <Button
            size="icon"
            variant="ghost"
            aria-label="Previous line"
            title="Previous line (←)"
            disabled={loading}
            onClick={() => void player.step(-1)}
          >
            <SkipBack />
          </Button>
          <Button
            size="icon"
            variant="secondary"
            onClick={() => player.togglePause()}
            disabled={loading}
            title="Play / pause (Space)"
          >
            {loading ? <LoaderCircle className="animate-spin" /> : player.status === 'paused' ? <Play /> : <Pause />}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            aria-label="Next line"
            title="Next line (→)"
            disabled={loading}
            onClick={() => void player.step(1)}
          >
            <SkipForward />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            aria-label="Replay line"
            title="Replay this line (R)"
            disabled={loading || !line}
            onClick={() => void player.replay()}
          >
            <RotateCcw />
          </Button>
        </div>
        <div className="grid min-w-0 flex-1 text-sm">
          <span className="truncate text-xs text-muted-foreground">{status}</span>
          <span className="truncate">{line ? (spoken ? line.text : `(${line.text})`) : '…'}</span>
        </div>
        {line &&
          (onEdit ? (
            <Button size="sm" variant="ghost" title="Edit this line (E)" onClick={() => onEdit(Number(line.id))}>
              <Pencil /> Edit
            </Button>
          ) : (
            spoken && (
              <Button size="sm" variant="ghost" asChild>
                <Link href={adminLines.show({ productionId, id: Number(line.id) })}>
                  <SlidersHorizontal /> Fix this line
                </Link>
              </Button>
            )
          ))}
        <Button size="icon" variant="ghost" aria-label="Stop" title="Stop (Esc)" onClick={player.stop}>
          <Square />
        </Button>
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <div className="flex rounded-md border p-0.5">
          {(['line', 'continuous'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => player.setMode(mode)}
              className={`rounded px-2 py-0.5 ${player.mode === mode ? 'bg-secondary text-foreground' : 'hover:text-foreground'}`}
            >
              {mode === 'line' ? 'Line by line' : 'Continuous'}
            </button>
          ))}
        </div>
        <span className="hidden sm:inline">
          {lineByLine ? 'Stops after each line. ' : ''}Space play/pause · ← → lines · R replay
          {onEdit ? ' · E edit' : ''} · Esc stop
        </span>
      </div>
    </div>
  )
}
